import { useRouter } from 'next/router';
import ImageWithFallback from '../../../common/ImageWithFallback';
import ContentWrapper from '../../../common/ContentWrapper';
import type { Hotel } from '../../../../core/isValidHotel';
import type { OwnerProfile } from '../../../../../types/MiniSiteContents';
import { Locale } from '../../../../core/constants';
import isAbsoluteUrl from '../../../../core/helpers/isAbsoluteUrl';
import useRelativeUrl from '../../../../core/hooks/useRelativeUrl';
import Markdown from '../../../common/Markdown/Markdown';

type Props = {
  hotel: Hotel;
  miniSiteContent: OwnerProfile;
};

const MiniSiteOwnerProfile = ({ hotel, miniSiteContent }: Props) => {
  const router = useRouter();
  const { locale } = router;
  const { name: hotelName } = hotel;
  const { contentTitle, ownerName, ownerTitle, description, photoUrl, link } = miniSiteContent;
  const { getRelativeUrl } = useRelativeUrl();

  return (
    <ContentWrapper className="w-full">
      {contentTitle && <p className="pb-6 text-title3 font-bold">{contentTitle}</p>}

      {/* desktop view */}
      <div className="hidden rounded-lg bg-white md:block">
        <div className="flex items-center gap-12 px-32 py-12">
          <div className="relative size-24 flex-none">
            <ImageWithFallback
              alt="mini-site-owner-image"
              className="rounded-full"
              src={photoUrl || '/fallback-img-1440x440.png'}
              layout="fill"
              fallbackSrc="/fallback-img-1440x440.png"
              url={link && (isAbsoluteUrl(link) ? link : getRelativeUrl(link))}
              target={isAbsoluteUrl(link) ? '_blank' : '_self'}
            />
          </div>
          <div className="flex grow flex-col gap-2.5">
            <div className="flex flex-row gap-2.5">
              <p className="text-small">{hotelName}</p>
              <p className="text-small">{ownerTitle}</p>
            </div>
            <div className="flex gap-2">
              <p className="font-bold">{ownerName}</p>
              {locale === Locale.JAPANESE && <p>さん</p>}
            </div>
            <Markdown className="text-small">{description}</Markdown>
          </div>
        </div>
      </div>

      {/* mobile view */}
      <div className="rounded-lg bg-white md:hidden">
        <div className="flex flex-col gap-4 p-6 sm:p-8">
          <div className="flex justify-center gap-5">
            <div className="relative size-16 flex-none self-center sm:size-24">
              <ImageWithFallback
                alt="mini-site-owner-image"
                className="rounded-full"
                src={photoUrl || '/fallback-img-1440x440.png'}
                layout="fill"
                fallbackSrc="/fallback-img-1440x440.png"
                url={link && (isAbsoluteUrl(link) ? link : getRelativeUrl(link))}
                target={isAbsoluteUrl(link) ? '_blank' : '_self'}
              />
            </div>
            <div className="flex flex-col gap-3">
              <div className="flex flex-row gap-3">
                <p className="text-small">{hotelName}</p>
                <p className="text-small">{ownerTitle}</p>
              </div>
              <div className="flex gap-2">
                <p className="font-bold">{ownerName}</p>
                {locale === Locale.JAPANESE && <p>さん</p>}
              </div>
            </div>
          </div>
          <div>
            <Markdown className="text-small">{description}</Markdown>
          </div>
        </div>
      </div>
    </ContentWrapper>
  );
};

export default MiniSiteOwnerProfile;
